"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Bell, Moon, Globe, Shield, TriangleAlert, Loader2, CalendarX } from "lucide-react";
import { toast } from "sonner";
import { useAuth, DeleteAccountError } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// ── Preflight data ─────────────────────────────────────────────────────────

interface AccountSummary {
  upcomingBookings: number;
  totalClients: number;
  planName: "free" | "pro";
}

async function fetchAccountSummary(): Promise<AccountSummary> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { upcomingBookings: 0, totalClients: 0, planName: "free" };

  const { data: business } = await supabase
    .from("business_profiles")
    .select("id, plan")
    .eq("user_id", user.id)
    .maybeSingle();

  if (!business) return { upcomingBookings: 0, totalClients: 0, planName: "free" };

  const today = new Date().toISOString().split("T")[0];

  const [{ count: upcomingBookings }, { count: totalClients }] = await Promise.all([
    supabase
      .from("bookings")
      .select("id", { count: "exact", head: true })
      .eq("business_id", business.id)
      .eq("status", "confirmed")
      .gte("booking_date", today),
    supabase
      .from("clients")
      .select("id", { count: "exact", head: true })
      .eq("business_id", business.id),
  ]);

  return {
    upcomingBookings: upcomingBookings ?? 0,
    totalClients: totalClients ?? 0,
    planName: business.plan === "pro" ? "pro" : "free",
  };
}

// ── Component ─────────────────────────────────────────────────────────────

export default function Settings() {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Preflight summary (loaded when Danger Zone card mounts)
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const summaryLoadedRef = useRef(false);

  useEffect(() => {
    if (summaryLoadedRef.current) return;
    summaryLoadedRef.current = true;
    fetchAccountSummary().then(setSummary);
  }, []);

  const canConfirm = confirmText === "DELETE" && !isDeleting;

  async function handleDelete() {
    if (!canConfirm) return;
    setIsDeleting(true);

    try {
      await deleteAccount();
      // deleteAccount() navigates to / on success — no further action needed
      toast.success("Your account has been permanently deleted.");
    } catch (err: unknown) {
      const e = err as DeleteAccountError;

      if (e.code === "UPCOMING_BOOKINGS") {
        toast.error(`You have ${e.count} upcoming appointment${e.count !== 1 ? "s" : ""}. Please cancel them before deleting your account.`);
        setDialogOpen(false);
        navigate("/dashboard/bookings");
        return;
      }
      if (e.code === "PENDING_PAYMENTS") {
        toast.error(`You have ${e.count} booking${e.count !== 1 ? "s" : ""} with pending payments. Please resolve them first.`);
      } else if (e.code === "STRIPE_CANCEL_FAILED") {
        toast.error("We couldn't cancel your subscription. Please visit billing settings or contact support@getfiggy.link.");
      } else if (e.code === "ALREADY_IN_PROGRESS") {
        toast.info("Account deletion is already underway. This may take a moment.");
      } else {
        toast.error("Something went wrong and your account was not deleted. Please try again or contact support@getfiggy.link.");
      }
    } finally {
      setIsDeleting(false);
      setConfirmText("");
    }
  }

  function handleOpenDialog() {
    setConfirmText("");
    setDialogOpen(true);
  }

  const hasUpcoming = (summary?.upcomingBookings ?? 0) > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold">Settings</h1>
            <p className="text-muted-foreground">Manage your account preferences and settings</p>
          </div>

          <div className="space-y-6">
            {/* Notifications */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Notifications</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-notifications">Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive email updates about your bookings</p>
                  </div>
                  <Switch id="email-notifications" defaultChecked />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="booking-reminders">Booking Reminders</Label>
                    <p className="text-sm text-muted-foreground">Get reminders about upcoming appointments</p>
                  </div>
                  <Switch id="booking-reminders" defaultChecked />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="marketing-emails">Marketing Emails</Label>
                    <p className="text-sm text-muted-foreground">Receive updates about new features and tips</p>
                  </div>
                  <Switch id="marketing-emails" />
                </div>
              </div>
            </Card>

            {/* Appearance */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Moon className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Appearance</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="dark-mode">Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">Use dark theme throughout the application</p>
                  </div>
                  <Switch id="dark-mode" />
                </div>
              </div>
            </Card>

            {/* Privacy & Security */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Privacy & Security</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="two-factor">Two-Factor Authentication</Label>
                    <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
                  </div>
                  <Switch id="two-factor" />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="profile-visibility">Public Profile</Label>
                    <p className="text-sm text-muted-foreground">Make your profile visible to others</p>
                  </div>
                  <Switch id="profile-visibility" defaultChecked />
                </div>
              </div>
            </Card>

            {/* Language & Region */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-figgy-600" />
                <h2 className="text-lg font-semibold">Language & Region</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Language</Label>
                    <p className="text-sm text-muted-foreground">English (US)</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Timezone</Label>
                    <p className="text-sm text-muted-foreground">
                      {Intl.DateTimeFormat().resolvedOptions().timeZone}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* ── Danger Zone ─────────────────────────────────────────────── */}
            <div className="border border-red-200 rounded-xl bg-red-50/40 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-1">
                  <TriangleAlert className="h-4 w-4 text-red-600" />
                  <h2 className="text-base font-semibold text-red-900">Danger Zone</h2>
                </div>
                <p className="text-sm text-red-700/80 mb-5 leading-relaxed">
                  Permanently and irreversibly deletes your Figgy account, all client records, bookings, and business data.
                  {summary?.planName === "pro" && " Your Pro subscription will be canceled immediately."}
                </p>

                {/* Preflight summary */}
                {summary && (
                  <div className="flex flex-wrap gap-4 mb-5">
                    {hasUpcoming && (
                      <div className="flex items-center gap-1.5 text-sm text-red-700 font-medium">
                        <CalendarX className="h-4 w-4" />
                        {summary.upcomingBookings} upcoming appointment{summary.upcomingBookings !== 1 ? "s" : ""}
                      </div>
                    )}
                    {summary.totalClients > 0 && (
                      <div className="text-sm text-red-700/70">
                        {summary.totalClients.toLocaleString()} client record{summary.totalClients !== 1 ? "s" : ""} will be deleted
                      </div>
                    )}
                  </div>
                )}

                {hasUpcoming && (
                  <p className="text-xs text-red-600 mb-4 font-medium">
                    You must cancel all upcoming appointments before deleting your account.
                  </p>
                )}

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleOpenDialog}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Confirmation dialog ────────────────────────────────────────────── */}
      <AlertDialog open={dialogOpen} onOpenChange={(open) => { if (!isDeleting) setDialogOpen(open); }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <TriangleAlert className="h-5 w-5" />
              Delete your Figgy account?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p className="text-muted-foreground">This will permanently delete:</p>
                <ul className="space-y-1.5 text-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">–</span>
                    Your business profile and booking link
                  </li>
                  {summary && summary.totalClients > 0 && (
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground mt-0.5">–</span>
                      {summary.totalClients.toLocaleString()} client record{summary.totalClients !== 1 ? "s" : ""}
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">–</span>
                    All past bookings, services, and availability
                  </li>
                  {summary?.planName === "pro" && (
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground mt-0.5">–</span>
                      Your Pro subscription (canceled immediately)
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">–</span>
                    All uploaded files and booking cards
                  </li>
                </ul>
                <p className="font-medium text-foreground">This cannot be undone or recovered.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="delete-confirm" className="text-sm font-medium">
                    Type <span className="font-mono font-bold">DELETE</span> to confirm
                  </Label>
                  <Input
                    id="delete-confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DELETE"
                    disabled={isDeleting}
                    autoComplete="off"
                    className="font-mono"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canConfirm}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting account…
                </>
              ) : (
                "Permanently Delete Account"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
